import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FlaskConical,
  Microscope,
  Dna,
  Snowflake,
  Atom,
  Beaker,
  TestTubes,
  Boxes,
  Building2,
  Recycle,
  Handshake,
  ArrowLeftRight,
  ChevronLeft,
} from 'lucide-react';

/*
  صفحة "المراكز البحثية" — صفحة منتجات (واجهة فقط، بلا اعتماد على الباك-اند).
  تزويد وتبادل الكواشف والمعدات والمواد الحيوية بين المراكز والمختبرات البحثية.
*/

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

const categories = [
  { id: 'chem', icon: FlaskConical, title: 'كيماويات خام', en: 'Reagents & Solvents' },
  { id: 'equip', icon: Microscope, title: 'معدات متخصصة', en: 'Specialized Equipment' },
  { id: 'bio', icon: Dna, title: 'مواد حيوية', en: 'Biological Materials' },
  { id: 'short', icon: Snowflake, title: 'مواد محدودة الصلاحية', en: 'Short Shelf-life' },
];

const products = [
  {
    icon: FlaskConical, cat: 'chem',
    name: 'محلول كاشف Tris-HCl Buffer',
    en: '1 L · pH 7.4 · Molecular Biology Grade',
    tag: 'كيماويات خام', price: '٢٤٠ ر.س', mode: 'بيع', condition: 'جديد',
    center: 'مختبر الكيمياء التحليلية',
    grad: 'from-brand-blue/15 to-brand-lightBlue/10', ico: 'text-brand-blue',
  },
  {
    icon: Microscope, cat: 'equip',
    name: 'جهاز طرد مركزي Centrifuge 5424R',
    en: 'Refrigerated · 21,130 × g',
    tag: 'معدات متخصصة', price: 'للتبادل', mode: 'تبادل', condition: 'مستعمل — ممتاز',
    center: 'وحدة الأجهزة الدقيقة',
    grad: 'from-brand-blueSecondary/15 to-brand-blue/10', ico: 'text-brand-blueSecondary',
  },
  {
    icon: Atom, cat: 'equip',
    name: 'مطياف ضوئي UV-Vis Spectrophotometer',
    en: '190–1100 nm · Dual Beam',
    tag: 'معدات متخصصة', price: '١٨٬٥٠٠ ر.س', mode: 'بيع', condition: 'جديد',
    center: 'المختبر المركزي للأبحاث',
    grad: 'from-brand-blue/15 to-brand-green/10', ico: 'text-brand-blue',
  },
  {
    icon: Dna, cat: 'bio',
    name: 'خط خلايا HeLa Cell Line',
    en: 'Passage 12 · Mycoplasma-free',
    tag: 'مواد حيوية', price: 'للتبادل', mode: 'تبادل', condition: 'موثّق',
    center: 'مركز علوم الحياة',
    grad: 'from-brand-green/15 to-secondary-300/10', ico: 'text-brand-green',
  },
  {
    icon: TestTubes, cat: 'bio',
    name: 'سلالة بكتيرية E. coli DH5α',
    en: 'Competent Cells · –80°C',
    tag: 'مواد حيوية', price: '٤٨٠ ر.س', mode: 'بيع', condition: 'جديد',
    center: 'مختبر الأحياء الدقيقة',
    grad: 'from-secondary-400/15 to-brand-green/10', ico: 'text-brand-green',
  },
  {
    icon: Beaker, cat: 'short',
    name: 'إيثانول مطلق Absolute Ethanol',
    en: '2.5 L · ≥99.8% · فائض',
    tag: 'محدود الصلاحية', price: 'للتبادل', mode: 'تبادل', condition: 'فائض — غير مفتوح',
    center: 'مركز أبحاث المواد',
    grad: 'from-brand-lightBlue/15 to-brand-blue/10', ico: 'text-brand-blue',
  },
  {
    icon: Boxes, cat: 'chem',
    name: 'أطباق بتري معقّمة (٥٠٠ طبق)',
    en: '90 mm · Sterile · Disposable',
    tag: 'مستهلكات', price: '٣٢٠ ر.س', mode: 'بيع', condition: 'جديد',
    center: 'مستودع المستلزمات البحثية',
    grad: 'from-accent-100 to-brand-blue/10', ico: 'text-brand-blueSecondary',
  },
  {
    icon: Microscope, cat: 'equip',
    name: 'ميكروسكوب فلوري Fluorescence',
    en: 'Inverted · LED · 4 Channels',
    tag: 'معدات متخصصة', price: 'للتبادل', mode: 'تبادل', condition: 'مستعمل — جيد جداً',
    center: 'وحدة المجهر والتصوير',
    grad: 'from-brand-blue/15 to-brand-blueSecondary/10', ico: 'text-brand-blue',
  },
];

const exchangeSteps = [
  { icon: Boxes, title: 'اعرض الفائض', desc: 'يضيف المركز كواشفه أو معداته الفائضة على المنصة.' },
  { icon: ArrowLeftRight, title: 'اطلب وتبادل', desc: 'يطلب مركزٌ آخر المادة ويتفق الطرفان على شروط التبادل.' },
  { icon: Recycle, title: 'قلّل الهدر', desc: 'تُنقل المادة وتُستفاد بدل أن تُهدر — أثر مالي وبيئي مباشر.' },
];

const ResearchCenters = () => {
  const [activeCat, setActiveCat] = useState('all');

  useEffect(() => {
    document.title = 'المراكز البحثية | بديل';
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(
    () => (activeCat === 'all' ? products : products.filter((p) => p.cat === activeCat)),
    [activeCat]
  );

  return (
    <div className="bg-white text-brand-dark" dir="rtl">
      {/* ---------- Simple header (مثل صفحة المنتجات) ---------- */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-brand-blueSecondary to-brand-green" />
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(0deg,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[length:40px_40px]" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="text-center text-white max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">المراكز البحثية</h1>
            <p className="text-lg sm:text-xl text-white/85 leading-relaxed">
              منصّة لتزويد وتبادل الكواشف والمعدات والمواد الحيوية بين المراكز والمختبرات البحثية —
              كل ما يحتاجه الباحث في مكان واحد، مع إمكانية بيع الفائض أو تبادله.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ---------- Categories quick cards ---------- */}
      <section className="container mx-auto px-4 py-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((c) => (
            <motion.button
              key={c.id}
              variants={fadeUp}
              onClick={() => setActiveCat(activeCat === c.id ? 'all' : c.id)}
              className={`text-right rounded-2xl border p-5 transition-all ${
                activeCat === c.id
                  ? 'border-brand-blue bg-brand-blue/5 shadow-md'
                  : 'border-accent-100 bg-white hover:shadow-md hover:border-brand-blue/40'
              }`}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-blue to-brand-green flex items-center justify-center shadow-sm mb-4">
                <c.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-brand-dark">{c.title}</h3>
              <p className="font-mono text-[11px] text-brand-blue/70 mt-0.5">{c.en}</p>
            </motion.button>
          ))}
        </motion.div>
      </section>

      {/* ---------- Filter chips + product grid ---------- */}
      <section className="container mx-auto px-4 pb-16">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <h2 className="text-2xl font-bold">المنتجات المتاحة</h2>
          <div className="flex flex-wrap gap-2">
            <Chip active={activeCat === 'all'} onClick={() => setActiveCat('all')}>الكل</Chip>
            {categories.map((c) => (
              <Chip key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)}>{c.title}</Chip>
            ))}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((p) => (
            <motion.div
              key={p.name}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5 }}
              className="group rounded-2xl bg-white border border-accent-100 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col"
            >
              <div className={`relative h-40 bg-gradient-to-br ${p.grad} flex items-center justify-center`}>
                <p.icon className={`w-16 h-16 ${p.ico} opacity-80 group-hover:scale-110 transition-transform`} strokeWidth={1.4} />
                <span className={`absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full ${p.mode === 'تبادل' ? 'bg-brand-green text-white' : 'bg-brand-blue text-white'}`}>
                  {p.mode}
                </span>
                <span className="absolute top-3 left-3 text-[11px] font-medium px-2.5 py-1 rounded-full bg-white/85 backdrop-blur text-accent-700 border border-white">
                  {p.tag}
                </span>
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-bold text-sm text-brand-dark leading-snug line-clamp-2">{p.name}</h3>
                <p className="font-mono text-[11px] text-accent-500 mt-1">{p.en}</p>
                <div className="flex items-center gap-1.5 mt-3 text-[11px] text-accent-500">
                  <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="line-clamp-1">{p.center}</span>
                </div>
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <span className={`font-bold ${p.mode === 'تبادل' ? 'text-brand-green' : 'text-brand-blue'}`}>{p.price}</span>
                  <span className="text-[11px] rounded-md bg-accent-50 text-accent-600 px-2 py-1 border border-accent-100">{p.condition}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ---------- تبادل المواد الفائضة (قسم بسيط) ---------- */}
      <section className="bg-accent-50/60 border-y border-accent-100">
        <div className="container mx-auto px-4 py-14">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold">تبادل المواد الفائضة بين المراكز</h2>
            <p className="mt-3 text-accent-600">
              مركزٌ لديه كاشف أو جهاز فائض يعرضه، فيستفيد منه مركزٌ آخر بدل أن يُهدر — بثلاث خطوات بسيطة.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {exchangeSteps.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl bg-white border border-accent-100 p-6">
                <span className="absolute top-5 left-5 font-mono text-3xl font-bold text-accent-100">{`٠${i + 1}`}</span>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-blue to-brand-green flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-brand-dark">{s.title}</h4>
                <p className="mt-2 text-sm text-accent-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Simple CTA ---------- */}
      <section className="container mx-auto px-4 py-16">
        <div className="rounded-2xl bg-gradient-to-l from-brand-blue to-brand-green p-8 sm:p-10 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold">هل تمثّل مركزاً أو مختبراً بحثياً؟</h2>
          <p className="mt-3 text-white/85 max-w-xl mx-auto">
            انضمّ إلى بديل لتزويد احتياجاتك البحثية أو عرض موادك الفائضة للتبادل والبيع.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3 font-bold text-brand-blue hover:-translate-y-0.5 transition-transform">
              <Handshake className="w-5 h-5" /> تواصل معنا
            </Link>
            <Link to="/products" className="inline-flex items-center gap-2 rounded-xl border-2 border-white/70 px-7 py-3 font-bold text-white hover:bg-white/10 transition-colors">
              تصفّح كل المنتجات <ChevronLeft className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
      active
        ? 'bg-brand-blue text-white border-brand-blue'
        : 'bg-white text-accent-600 border-accent-200 hover:border-brand-blue/50 hover:text-brand-blue'
    }`}
  >
    {children}
  </button>
);

export default ResearchCenters;
